import classnames from "classnames";
import CSSTransitionGroup from "react-addons-css-transition-group";
import keycode from "keycode";
import prefixCss from "utils/prefixCss";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import SliderTimeMarker from "components/SliderTimeMarker";
import { autobind } from "core-decorators";

import styles from "./SliderStyles.css";

export default class Slider extends PureComponent {
  static propTypes = {
    adPlaying: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    hideProgress: PropTypes.bool.isRequired,
    onSlideProgress: PropTypes.func,
    onSlideStart: PropTypes.func,
    onSlideStop: PropTypes.func,
    scrubbing: PropTypes.bool.isRequired,
    seekBack: PropTypes.func,
    seekForward: PropTypes.func,
    seeking: PropTypes.bool.isRequired,
    sliderWidth: PropTypes.number.isRequired,
    timeMarkerWidth: PropTypes.number.isRequired,
    valueMax: PropTypes.number.isRequired,
    valueNow: PropTypes.number.isRequired,
  };

  state = {
    showCurrentTimeMarker: false,
    mouseOver: false,
    mouseX: 0,
  };

  componentWillUnmount() {
    if (this.currentTimeMarkerTimeout) {
      clearTimeout(this.currentTimeMarkerTimeout);
    }

    this.unbindMouseMoveEvents();
  }

  @autobind
  handleShowSeekMaker(event) {
    // Touch devices receive a `mousemove` on `touchend` and we want to ignore it.
    if (this.hasReceivedTouchEvent) return;

    if (event.target instanceof HTMLElement) {
      const x =
        (event.clientX || event.pageX) -
        event.target.getBoundingClientRect().left;

      this.setState({
        mouseOver: true,
        mouseX: x,
      });
      this.showCurrentTimeMarker();
    }
  }

  @autobind
  handleMouseLeave() {
    this.setState({ mouseOver: false });
  }

  showCurrentTimeMarker() {
    this.setState({
      showCurrentTimeMarker: true,
    });
    if (this.currentTimeMarkerTimeout) {
      clearTimeout(this.currentTimeMarkerTimeout);
    }

    this.currentTimeMarkerTimeout = setTimeout(() => {
      this.hideCurrentTimeMarker();
    }, 2000);
  }

  hideCurrentTimeMarker() {
    if (this.currentTimeMarkerTimeout) {
      clearTimeout(this.currentTimeMarkerTimeout);
    }
    this.setState({
      showCurrentTimeMarker: false,
    });
  }

  calculateTimeMarkerLeft() {
    const sliderWidth = this.props.sliderWidth;
    const max = this.props.valueMax;
    const progressWidth = (sliderWidth * this.props.valueNow) / max;
    return this.clampTimeMarkerPosition(progressWidth);
  }

  clampTimeMarkerPosition(targetX) {
    const sliderWidth = this.props.sliderWidth;
    const timeMarkerWidth = this.props.timeMarkerWidth;

    if (targetX < timeMarkerWidth / 2) {
      return timeMarkerWidth / 2;
    }
    if (targetX + timeMarkerWidth / 2 > sliderWidth) {
      return sliderWidth - timeMarkerWidth / 2;
    }
    return targetX;
  }

  renderSeekTimeMarker() {
    if (
      !this.props.hideProgress &&
      this.state.mouseOver &&
      !this.props.scrubbing
    ) {
      const style = prefixCss(
        "transform",
        `translate3d(${this.clampTimeMarkerPosition(
          this.state.mouseX,
        )}px, -50%, 0)`,
      );
      const max = this.props.valueMax;
      const time = (this.state.mouseX / this.props.sliderWidth) * max;

      return <SliderTimeMarker mouseOver style={style} time={time} />;
    }
  }

  renderCurrentTimeMarker() {
    if (
      !this.props.hideProgress &&
      (this.props.scrubbing ||
        this.state.showCurrentTimeMarker ||
        this.state.mouseOver)
    ) {
      const style = prefixCss(
        "transform",
        `translate3d(${this.calculateTimeMarkerLeft()}px, -50%, 0)`,
      );

      return (
        <SliderTimeMarker
          seeking={this.props.scrubbing}
          style={style}
          time={this.props.valueNow}
        />
      );
    }
  }

  @autobind
  handleMouseDown(event) {
    event.persist();

    if (event.type.toLowerCase().indexOf("touch") !== -1) {
      this.hasReceivedTouchEvent = true;
    } else if (this.hasReceivedTouchEvent) {
      return;
    }

    if (event.currentTarget instanceof HTMLElement) {
      const target = event.currentTarget;
      this.hitAreaBoundingClientRect = target.getBoundingClientRect();
    }

    this.bindMouseMoveEvents();
    this.props.onSlideStart();

    this.previousTimeSlideValue = this.getValueNowFromMouseEvent(event);
    this.props.onSlideProgress(this.previousTimeSlideValue);
  }

  @autobind
  handleMouseMove(event) {
    this.previousTimeSlideValue = this.getValueNowFromMouseEvent(event);
    this.props.onSlideProgress(this.previousTimeSlideValue);
  }

  @autobind
  handleMouseUp() {
    this.unbindMouseMoveEvents();
    this.props.onSlideStop(this.previousTimeSlideValue || 0);
  }

  getValueNowFromMouseEvent(event) {
    const constHitAreaBoundingClientRect = this.hitAreaBoundingClientRect;

    if (event.nativeEvent) {
      event = event.nativeEvent;
    }

    if (constHitAreaBoundingClientRect) {
      const x = (() => {
        if (event instanceof MouseEvent) {
          return event.clientX;
        }

        const touches = event.touches;
        const touch = touches.item(touches.length - 1);

        if (!touch) {
          throw new Error("No touch list supplied with touch event");
        }

        return touch.clientX;
      })();

      const relativeX = Math.max(
        0,
        Math.min(
          x - constHitAreaBoundingClientRect.left,
          constHitAreaBoundingClientRect.width,
        ),
      );
      const percentage = relativeX / constHitAreaBoundingClientRect.width;
      return percentage * this.props.valueMax;
    }

    throw new Error("Mouse event received without clientRect being saved");
  }

  bindMouseMoveEvents() {
    window.addEventListener("mousemove", this.handleMouseMove);
    window.addEventListener("touchcancel", this.handleMouseMove);
    window.addEventListener("touchmove", this.handleMouseMove);

    window.addEventListener("mouseup", this.handleMouseUp);
    window.addEventListener("touchend", this.handleMouseUp);
  }

  unbindMouseMoveEvents() {
    window.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("touchcancel", this.handleMouseMove);
    window.removeEventListener("touchmove", this.handleMouseMove);

    window.removeEventListener("mouseup", this.handleMouseUp);
    window.removeEventListener("touchend", this.handleMouseUp);
  }

  get isBusy() {
    return (
      this.props.valueNow === 0 ||
      Math.abs(this.props.valueNow - this.props.valueMax) < 16
    );
  }

  handleKeyUp = (event) => {
    switch (keycode(event).toLowerCase()) {
      case "left":
        this.props.seekBack();
        break;
      case "right":
        this.props.seekForward();
        break;
    }
  };

  render() {
    const sliderWrapClass = classnames(styles.root, {
      [styles.disabled]: this.props.disabled,
      [styles.hideProgress]: this.props.hideProgress,
    });

    return (
      <div className={sliderWrapClass} disabled={this.props.disabled}>
        <CSSTransitionGroup
          transitionEnterTimeout={300}
          transitionLeaveTimeout={300}
          transitionName="verse-time-marker"
        >
          {this.renderCurrentTimeMarker()}
        </CSSTransitionGroup>
        <CSSTransitionGroup
          transitionEnterTimeout={300}
          transitionLeaveTimeout={300}
          transitionName="verse-time-marker"
        >
          {this.renderSeekTimeMarker()}
        </CSSTransitionGroup>

        <div className={styles.overflowWrap}>
          <div
            className={classnames(styles.slider, {
              [styles.adPlaying]: this.props.adPlaying,
            })}
          >
            <div
              aria-busy={this.isBusy}
              aria-label="Playback Slider"
              aria-valuemax={this.props.valueMax / 1000}
              aria-valuemin={0}
              aria-valuenow={Math.floor(this.props.valueNow / 1000)}
              className={styles.sliderHit}
              onKeyUp={this.handleKeyUp}
              onMouseDown={this.handleMouseDown}
              onMouseEnter={this.handleShowSeekMaker}
              onMouseLeave={this.handleMouseLeave}
              onMouseMove={this.handleShowSeekMaker}
              onTouchStart={this.handleMouseDown}
              role="slider"
              tabIndex={0}
            />

            <div className={styles.background}>
              {!this.props.hideProgress && (
                <div
                  className={styles.foreground}
                  style={{
                    width: `${
                      (this.props.valueNow / this.props.valueMax) * 100
                    }%`,
                  }}
                />
              )}
              {!this.props.hideProgress && <div className={styles.progress} />}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
